/**
 * Shapes returned by c14-analytics (the fields this portal uses). Mirrors
 * packages/modules/c14-analytics/src/service/{kpi,alerts,extract}.ts
 * field-for-field — this module intentionally exposes no raw-fact/SQL
 * endpoint, so every shape below is already an aggregate.
 */

export interface SlaComplianceByMda {
  mdaCode: string;
  totalClosed: number;
  breached: number;
  compliancePct: number | null;
}

export interface SlaComplianceResult {
  totalClosed: number;
  breached: number;
  nonBreached: number;
  compliancePct: number | null;
  /** Present only on the unfiltered (no ?mda=) call. */
  byMda?: SlaComplianceByMda[];
}

export interface VolumesByMda {
  mdaCode: string;
  count: number;
}

export interface VolumesResult {
  total: number;
  byChannel: Array<{ channel: 'DIGITAL' | 'COUNTER'; count: number }>;
  byMda?: VolumesByMda[];
}

export interface RevenueByMda {
  mdaCode: string;
  currency: string;
  amount: number;
}

export interface RevenueResult {
  byCurrency: Array<{ currency: string; amount: number; payments: number }>;
  byMda?: RevenueByMda[];
}

export interface AdoptionSlice {
  digital: number;
  counter: number;
  total: number;
  digitalRatioPct: number | null;
}

export interface AdoptionResult {
  serviceUsage: AdoptionSlice;
  cases: AdoptionSlice;
}

export type HeatmapMetric = 'cases' | 'usage';

export interface HeatmapRow {
  district: string;
  /** Only present when the query was grouped `by=service`; this portal groups by district alone. */
  serviceCode?: string;
  count: number;
}

export interface HeatmapResult {
  metric: HeatmapMetric;
  groupedBy: string[];
  /** Districts below the k-anonymity threshold are omitted entirely from this array, not zeroed — see service/kanonymity.ts. */
  items: HeatmapRow[];
  /** Count of cells the API withheld. Does not, and cannot, distinguish "1-4 records" from "0 records" — see KNOWN_DISTRICTS below. */
  suppressed: number;
}

export interface SlaBreachAlert {
  mdaCode: string;
  today: number;
  trailingDailyAvg: number;
  /** today / trailingDailyAvg; null when the trailing average is 0 (a real baseline of zero, not "no data"). */
  ratio: number | null;
  thresholdMultiplier: number;
}

export interface AlertsResult {
  generatedAt: string;
  windowDays: number;
  thresholdMultiplier: number;
  minAlertCount: number;
  evaluated: number;
  alerts: SlaBreachAlert[];
}

export interface NbsExtract {
  generatedAt: string;
  suppressionThreshold: number;
  casesByDistrict: Array<{ district: string; count: number }>;
  usageByDistrict: Array<{ district: string; count: number }>;
  /** MDA-level, never suppressed — an MDA is a government office, not a small group of citizens (see service/extract.ts). */
  revenueByMda: Array<{ mdaCode: string; currency: string; amount: number }>;
  adoption: { serviceUsageDigitalRatioPct: number | null; casesDigitalRatioPct: number | null };
  suppressedCells: number;
}

/**
 * Display names for known MDA codes — cosmetic only. c14 has no /dims
 * listing endpoint (by design: it only ever returns codes on fact rows), so
 * this is not data the API supplies; it just makes the league table and
 * alerts list readable. Falls back to the bare code for anything unmapped,
 * so an MDA this map doesn't know about still renders correctly.
 */
export const MDA_NAMES: Record<string, string> = {
  SRC: 'Seychelles Revenue Commission',
  IMM: 'Immigration & Civil Status Division',
  MOH: 'Ministry of Health',
  ELS: 'Employment & Labour Services',
  LAND: 'Land Registry',
  MTC: 'Ministry of Tourism & Culture',
};

export function mdaName(code: string): string {
  return MDA_NAMES[code] ?? code;
}

/**
 * Every district this portal knows about, for honest suppression display on
 * the heatmap. GET /heatmap represents a suppressed cell by omitting its key
 * entirely (see service/kanonymity.ts's suppressSmallGroups — it filters the
 * row out, it does not zero or null a field), so a district missing from the
 * API response could genuinely have zero activity OR 1-4 records — both are
 * "too few to disclose" under k-anonymity, and real statistical agencies
 * suppress both identically on purpose (otherwise comparing a suppressed
 * cell against a shown zero would itself leak the small count). This portal
 * therefore renders any known district absent from `items` with the same
 * placeholder rather than guessing which case it is.
 */
export const KNOWN_DISTRICTS = [
  'Beau Vallon',
  'English River',
  'Mont Fleuri',
  'Anse Royale',
  'Baie Lazare',
  'Anse Boileau',
  'Takamaka',
  'Glacis',
  'Port Glaud',
  'La Digue',
] as const;

export function pct(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${v}%`;
}
